<!-- eslint-disable vue/no-unused-refs -->
<template>
    <div
        id="subscription-dropdown-tooltip"
        ref="subscriptionDropdownWrapper"
        v-pendo-tooltip="{
            content: `${
                isImpersonating
                    ? $t('subscriptions.Impersonating subscription')
                    : $t('subscriptions.Active subscription')
            }: ${$activeSubscription.displayName}`,
            arrow: true,
            trigger: 'manual',
        }"
        :aria-label="isImpersonating
            ? $t('subscriptions.Impersonating subscription')
            : $t('subscriptions.Active subscription')"
        tabindex="-1"
        data-cy="subscription-dropdown"
        pendo-analytics="subscriptions-nav-item"
        @click="handleSubscriptionDropdownClick"
        @focusin="onWrapperFocusIn"
        @focusout="onWrapperFocusOut"
        @mouseenter="onWrapperFocusIn"
        @mouseleave="onWrapperFocusOut">
        <pendo-multiselect
            ref="subscriptionDropdown"
            :tabindex="!hasMultipleSubscriptions ? 0 : undefined"
            aria-labelledby="subscription-dropdown-tooltip"
            class="subscription-dropdown"
            :class="{ impersonating: isImpersonating }"
            label-key="displayName"
            value-key="id"
            sort-by="displayName"
            trigger-size="small"
            max-trigger-width="250px"
            min-menu-width="250px"
            max-menu-width="250px"
            group-options-key="subs"
            group-label-key="name"
            :searchable="isSearchable"
            :options="groupedSubList"
            :option-height="32"
            :value="$activeSubscription"
            :allow-empty="false"
            :popper-options="{ placement: 'bottom-end', class: 'subscription-dropdown__popper' }"
            :disabled="!hasMultipleSubscriptions"
            @open="$emit('open')"
            @close="$emit('close')"
            @select="handleSubscriptionChange">
            <template #optionGroup="{ option }">
                <h2 class="subscription-dropdown__group-header-label">
                    {{ option.name }}
                </h2>
                <pendo-divider height="16px" />
            </template>
            <template #selectedLabel>
                <pendo-icon
                    class="subscription-dropdown__trigger-icon"
                    type="adopt-subscriptions"
                    size="20" />
                <span class="subscription-dropdown__trigger-label">
                    {{ $activeSubscription.displayName }}
                </span>
            </template>
            <template #option="{ option, index }">
                <div
                    class="subscription-dropdown__option"
                    data-cy="subscription-dropdown-option">
                    <pendo-multiselect-option-label
                        :label="option.displayName"
                        :index="index"
                        tooltip-classes="subscription-dropdown__tooltip"
                        class="subscription-dropdown__option-label" />
                </div>
            </template>
            <template #noResult>
                <div
                    data-cy="subscription-dropdown-no-results"
                    class="subscription-dropdown__no-result-container">
                    <div class="subscription-dropdown__no-result-header">
                        {{ $t('subscriptions.No subscriptions found.') }}
                    </div>
                    <div class="subscription-dropdown__no-result-body">
                        {{ $t('subscriptions.Try a different search.') }}
                    </div>
                </div>
            </template>
        </pendo-multiselect>
    </div>
</template>

<script>
import { PendoMultiselect, PendoMultiselectOptionLabel, PendoTooltip, PendoDivider, PendoIcon } from '@pendo/components-vue3';
import { mapGetters, useStore } from 'vuex';
import { computed } from 'vue';
import { useWindowSize } from '@vueuse/core';

export default {
    name: 'SubscriptionDropdown',
    components: {
        PendoMultiselect,
        PendoMultiselectOptionLabel,
        PendoDivider,
        PendoIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        isImpersonating: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['open', 'close'],
    setup () {
        const store = useStore();
        const { width: windowWidth } = useWindowSize();

        const subscriptionList = computed(() => {
            return store.getters['subscription/list'];
        });

        const isSearchable = computed(() => {
            if (windowWidth.value < 992) {
                return false;
            }

            return subscriptionList.value?.length > 10;
        });

        return {
            isSearchable,
        };
    },
    computed: {
        ...mapGetters({
            subscriptionList: 'subscription/list',
        }),
        hasMultipleSubscriptions () {
            return this.subscriptionList?.length > 1;
        },
        groupedSubList () {
            const subArray = [
                {
                    id: 'subscriptions',
                    name: this.$t('Subscriptions'),
                    subs: [],
                },
                {
                    id: 'oemSubscriptions',
                    name: this.$t('OEM Subscriptions'),
                    subs: [],
                },
            ];

            this.subscriptionList?.forEach((sub) => {
                if (sub.trainingPartnerSubscriptionId) {
                    subArray[1].subs.push(sub); // OEM Subscription
                } else {
                    subArray[0].subs.push(sub); // Regular Subscription
                }
            });

            return subArray;
        },
    },
    methods: {
        handleSubscriptionChange (sub) {
            this.$router.pendo.switchSubscription(sub.id);
        },
        handleSubscriptionDropdownClick ($event) {
            if (!this.isImpersonating) {
                return;
            }

            if ($event.metaKey || ($event.ctrlKey && $event.altKey)) {
                navigator.clipboard.writeText(this.$activeSubscription.name);
                this.toggleDropdown();
            }
        },
        onWrapperFocusIn () {
            this.$refs.subscriptionDropdownWrapper?._tooltip?.show();
        },
        onWrapperFocusOut () {
            this.$refs.subscriptionDropdownWrapper?._tooltip?.hide();
        },
        toggleDropdown () {
            this.$refs.subscriptionDropdown.toggleMenu();
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

.subscription-dropdown {
    :deep(.pendo-multiselect__trigger) {
        border: none;
        padding: 0 10px;
        margin: 0;
        color: var(--text-body-default-base);
        height: 32px;
        min-height: 32px;
        outline: none;
        background-color: var(--surface-default-background);
        min-width: unset !important;
        max-width: 180px !important;
        font-weight: 600;
        border-radius: 5px;
        display: flex;
        align-items: center;
        justify-content: center;

        &::after {
            display: none;
        }

        &:hover {
            background-color: var(--surface-secondary-hover);
        }

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -1px;
        }

        .screen-size(@zero, @large, {
            width: 32px;
            padding: 4px;
        });
    }

    :deep(.pendo-multiselect__value) {
        justify-content: center;
    }

    &.is-open {
        :deep(.pendo-multiselect__trigger) {
            background-color: var(--surface-secondary-hover);
        }
    }

    &.impersonating {
        :deep(.pendo-multiselect__trigger) {
            background-color: var(--static-nav-item-active);
            color: var(--static-text-body-inverse-base);

            &:hover {
                background-color: var(--static-nav-impersonating-background-base);
                color: var(--static-text-body-default-base);
            }
        }

        &.is-open {
            :deep(.pendo-multiselect__trigger) {
                background-color: var(--static-nav-impersonating-background-base);
                color: var(--static-text-body-default-base);
            }
        }

        :deep(.pendo-multiselect__input) {
            color: var(--static-text-body-default-base);
        }
    }

    &.is-disabled {
        :deep(.pendo-multiselect__trigger) {
            cursor: default;

            &:hover {
                background-color: transparent;
            }
        }

        &.impersonating {
            :deep(.pendo-multiselect__trigger) {
                background-color: var(--static-nav-item-active);
                color: var(--static-text-body-inverse-base);
            }
        }

        :deep(.pendo-multiselect__suffix) {
            display: none;
        }
    }

    :deep(.pendo-multiselect__suffix) {
        width: 12px;
        margin-left: 8px;

        &::before {
            opacity: unset;
            background: unset;
        }

        .screen-size(@zero, @large, {
            display: none;
        });
    }

    &__option {
        display: flex;
        align-items: center;
        width: 100%;
        box-sizing: border-box;
        line-height: 32px;
        cursor: pointer;
        user-select: none;
        border-radius: 5px;
        font-weight: 500;
        transition: background-color ease-in 150ms;

        &-label {
            flex: 1;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    :deep(.pendo-multiselect__input) {
        background-color: transparent;
    }

    :deep(.pendo-multiselect__search-icon-wrapper) {
        display: none;
    }

    &__no-result {
        &-container {
            display: flex;
            flex-direction: column;
        }

        &-header {
            font-weight: 500;
        }
    }

    &__trigger-icon {
        display: none;

        :deep(path) {
            stroke-width: 0.1;
        }

        .screen-size(@zero, @large, {
            display: flex;
        });
    }

    &__trigger-label {
        .screen-size(@zero, @large, {
            display: none;
        });
    }
}

.subscription-dropdown__popper {
    .subscription-dropdown__group-header-label {
        color: var(--text-body-subtle-base);
        font-size: 12px;
        text-transform: uppercase;
        font-family: 'DM Mono', monospace;
        margin: 0;
        padding: 4px 24px 4px 16px;
        letter-spacing: 0;
    }
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.subscription-dropdown__tooltip.v-popper__popper {
    z-index: 20002 !important;
}

.subscription-dropdown__popper {
    z-index: 20001; /* needs to go above side-panels, which some are set at 20000 to go over the RC */

    .pendo-multiselect__header {
        padding: 12px 12px 8px 20px;
        margin-bottom: 4px;
        border-bottom: 1px solid var(--palette-gray-30);
    }

    .pendo-multiselect__group {
        height: auto !important;
        padding: 0;
    }

    .pendo-multiselect__content-wrapper {
        padding: 0;

        .screen-size(@zero, @small, {
            max-height: calc(100vh - 60px) !important;
        });
    }

    .pendo-multiselect__scroll-container {
        padding: 8px 0px;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .pendo-multiselect__option {
        box-sizing: border-box;
        line-height: 32px;
        display: flex;
        cursor: pointer;
        user-select: none;
        border-radius: 5px;
        font-weight: 500;
        transition: background-color ease-in 150ms;
        margin: 0 16px 0 12px;
        padding: 6px 8px;

        &:hover,
        &.is-highlighted {
            background-color: var(--surface-default-hover);
        }

        & * {
            height: unset;
        }
    }

    .pendo-picker-popper__content {
        border-radius: 4px;
        border: 1px solid var(--surface-default-border);
        max-height: 400px !important;

        .screen-size(@zero, @small, {
            max-height: calc(100vh - 60px) !important;
        });
    }
}
</style>
